<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { autoUpdate, flip, offset, shift, useFloating, type Placement } from '@floating-ui/vue';
import { createFocusTrap, type FocusTrap } from 'focus-trap';
import { useElementBounding, usePreferredReducedMotion, useWindowSize } from '@vueuse/core';
import { isOfficeAddinHost } from '@/office/utils';
import { useTourStore } from '@/stores/tourStore';
import type { TourActionContext, TourDefinition } from '@/tours/types';
import { runTourActions } from '@/utils/tour/actions';
import { waitForTourElement } from '@/utils/tour/waitForElement';
import { logger } from '@/utils/logger';

/**
 * The visual half of the tour engine: dimmed backdrop with a spotlight
 * cutout, a floating scene bubble, focus/keyboard handling, target
 * resolution, and scripted playback. Nothing plays automatically — the
 * bubble describes a scene and waits; pressing NEXT hides the bubble, plays
 * the scene's script (real typing/clicking with the spotlight following),
 * then shows the next scene's bubble, so bubble and app state always match.
 * Scenes marked `interactive` leave their target clickable: the user's own
 * click advances the tour instead of the script.
 *
 * Safety invariant: the user can always leave — Esc works on every scene
 * (including mid-playback; scripts cancel between atomic actions), and a
 * target that never appears skips its scene instead of blocking.
 */

const SPOTLIGHT_PADDING = 6;

const store = useTourStore();
const route = useRoute();
const router = useRouter();

/** Scene anchor: what the bubble attaches to. */
const targetEl = ref<HTMLElement | null>(null);
/** Invitation modal root — used to focus its primary button on open. */
const inviteEl = ref<HTMLElement | null>(null);
/** Playback highlight: what the spotlight frames while an action runs. */
const holeTargetEl = ref<HTMLElement | null>(null);
const popoverEl = ref<HTMLElement | null>(null);
/** True once the current run has rendered at least one scene. */
const visible = ref(false);
/** True while navigating/looking up the current scene's target. */
const isResolving = ref(true);
/** True while a scene's script is playing (bubble hidden meanwhile). */
const isPlaying = ref(false);

const reducedMotion = usePreferredReducedMotion();
const animate = computed(() => reducedMotion.value !== 'reduce');

const step = computed(() => store.activeStep);
const stepBody = computed(() => {
  const body = step.value?.body;
  return typeof body === 'function' ? body() : body;
});
const totalSteps = computed(() => store.activeTour?.steps.length ?? 0);
const isLastStep = computed(() => totalSteps.value > 0 && store.stepIndex + 1 === totalSteps.value);

/**
 * The finale's "what's next" lists: every OTHER tour the user may run,
 * untaken ones first (the natural next step), already-taken ones after
 * (for a second look). Permission filtering comes with availableTours.
 */
const nextTours = computed(() => {
  const fresh: TourDefinition[] = [];
  const taken: TourDefinition[] = [];
  if (!isLastStep.value) {
    return { fresh, taken };
  }
  for (const tour of store.availableTours) {
    if (tour.key === store.activeTourKey) {
      continue;
    }
    (store.statuses[tour.key] ? taken : fresh).push(tour);
  }
  return { fresh, taken };
});

/** Finale tour-list click: wrap up the current tour, start the picked one. */
async function handleStartAnotherTour(key: string): Promise<void> {
  if (controlsLocked.value) {
    return;
  }
  // Reaching the finale counts as completing — run the current tour's
  // cleanup/navigation hooks before the next tour takes over.
  await store.complete();
  store.startTour(key);
}
const isCentered = computed(() => !step.value?.target || targetEl.value === null);
const showPopover = computed(
  () => visible.value && !isResolving.value && !isPlaying.value && step.value !== null
);
const controlsLocked = computed(() => isPlaying.value || isResolving.value);

const { width: viewportWidth, height: viewportHeight } = useWindowSize();

/** Spotlight follows the action being played, else the scene target. */
const holeEl = computed(() => holeTargetEl.value ?? targetEl.value);
const bounds = useElementBounding(holeEl);
/**
 * Ancestors that clip the target (overflow != visible). Collected once per
 * target: the `hole` computed re-runs on every scroll/resize tick, and
 * calling getComputedStyle up the whole chain per frame is wasted work —
 * the chain only changes when the target does.
 */
let clipAncestors: HTMLElement[] = [];
function collectClipAncestors(el: HTMLElement): HTMLElement[] {
  const found: HTMLElement[] = [];
  for (let node = el.parentElement; node && node !== document.documentElement;) {
    const style = getComputedStyle(node);
    if (/(auto|scroll|hidden|clip)/.test(`${style.overflowX}${style.overflowY}`)) {
      found.push(node);
    }
    node = node.parentElement;
  }
  return found;
}
// useElementBounding doesn't reliably re-measure when the ref switches to a
// DIFFERENT element (observed live: the hole stayed on the previous target).
// Force a measure on change, and again once smooth scrollIntoView settles.
watch(holeEl, el => {
  clipAncestors = el ? collectClipAncestors(el) : [];
  if (!el) {
    return;
  }
  bounds.update();
  setTimeout(() => {
    bounds.update();
  }, 450);
});

/** Spotlight cutout in viewport coordinates; null = fully dimmed backdrop. */
const hole = computed(() => {
  const el = holeEl.value;
  if (!el || bounds.width.value === 0 || bounds.height.value === 0) {
    return null;
  }
  // A target can overflow its scroll container (a row in a scroll-x table is
  // as wide as the table's CONTENT, not its visible box) — intersect with
  // every clipping ancestor so the spotlight never spills past what the user
  // actually sees. The ancestor list is pre-collected per target above.
  let left = bounds.left.value;
  let top = bounds.top.value;
  let right = left + bounds.width.value;
  let bottom = top + bounds.height.value;
  for (const ancestor of clipAncestors) {
    const rect = ancestor.getBoundingClientRect();
    left = Math.max(left, rect.left);
    top = Math.max(top, rect.top);
    right = Math.min(right, rect.right);
    bottom = Math.min(bottom, rect.bottom);
  }
  if (right - left <= 0 || bottom - top <= 0) {
    return null;
  }
  return {
    x: left - SPOTLIGHT_PADDING,
    y: top - SPOTLIGHT_PADDING,
    width: right - left + SPOTLIGHT_PADDING * 2,
    height: bottom - top + SPOTLIGHT_PADDING * 2,
  };
});

/**
 * Click blockers. Watch-only by default (one full-page shield); scenes
 * marked `interactive` leave the spotlight hole open so the user can click
 * the real target themselves.
 */
const shields = computed(() => {
  const vw = viewportWidth.value;
  const vh = viewportHeight.value;
  const h = hole.value;
  if (!step.value?.interactive || isPlaying.value || !h) {
    return [{ x: 0, y: 0, width: vw, height: vh }];
  }
  return [
    { x: 0, y: 0, width: vw, height: Math.max(h.y, 0) },
    { x: 0, y: h.y, width: Math.max(h.x, 0), height: h.height },
    { x: h.x + h.width, y: h.y, width: Math.max(vw - h.x - h.width, 0), height: h.height },
    { x: 0, y: h.y + h.height, width: vw, height: Math.max(vh - h.y - h.height, 0) },
  ];
});

const placement = computed<Placement>(() => step.value?.placement ?? 'bottom');
const { floatingStyles } = useFloating(targetEl, popoverEl, {
  placement,
  middleware: [
    // alignmentAxis: `-start`/`-end` placements line the bubble up with the
    // spotlight's edge (which sits SPOTLIGHT_PADDING outside the target), not
    // the target's own edge — otherwise it starts a few pixels off.
    offset({ mainAxis: 16, alignmentAxis: -SPOTLIGHT_PADDING }),
    // When neither side of the preferred axis fits (e.g. `right` on a
    // full-width card), fall through to the perpendicular axis instead of
    // overflowing the viewport. crossAxis off: a bubble that merely sticks out
    // along its alignment edge (a top-of-screen target) is shift's job, not a
    // reason to jump to another side.
    flip({ padding: 12, crossAxis: false, fallbackAxisSideDirection: 'start' }),
    // crossAxis lets the bubble slide along both axes to stay on screen. The
    // top/left margin never exceeds the spotlight's own distance from the
    // edge, so a target near the edge keeps its bubble flush with the frame.
    shift(({ rects }) => ({
      crossAxis: true,
      padding: {
        top: Math.min(12, Math.max(0, rects.reference.y - SPOTLIGHT_PADDING)),
        left: Math.min(12, Math.max(0, rects.reference.x - SPOTLIGHT_PADDING)),
        right: 12,
        bottom: 12,
      },
    })),
  ],
  whileElementsMounted: autoUpdate,
  // Position via top/left so the enter/leave transitions can own `transform`.
  transform: false,
});

// --- Scene resolution ------------------------------------------------------

let resolveToken = 0;
let interactiveCleanup: (() => void) | null = null;

function makeActionContext(token: number): TourActionContext {
  return {
    setHighlight: (el): void => {
      if (token === resolveToken) {
        holeTargetEl.value = el;
      }
    },
    isCancelled: (): boolean => token !== resolveToken || !store.activeTourKey,
    animate: animate.value,
  };
}

async function resolveStep(): Promise<void> {
  const token = ++resolveToken;
  isPlaying.value = false;
  holeTargetEl.value = null;
  interactiveCleanup?.();
  interactiveCleanup = null;

  const current = step.value;
  if (!current) {
    visible.value = false;
    isResolving.value = true;
    targetEl.value = null;
    return;
  }
  isResolving.value = true;

  if (current.prerequisite) {
    let ok = false;
    try {
      ok = await current.prerequisite();
    } catch {
      ok = false;
    }
    if (token !== resolveToken) {
      return;
    }
    if (!ok) {
      store.skipUnresolvableStep();
      return;
    }
  }

  if (current.route && route.name !== current.route) {
    // Preserve an outer hold (handleBack) — only release what we set.
    const heldByBack = store.isTourNavigation;
    store.isTourNavigation = true;
    try {
      await router.push({ name: current.route });
    } catch {
      // Guard redirected or navigation aborted — target lookup below decides.
    } finally {
      store.isTourNavigation = heldByBack;
    }
    if (token !== resolveToken) {
      return;
    }
  }

  if (current.onEnter) {
    // onEnter may navigate (e.g. pushing a parameterized section route) —
    // that is tour-driven navigation, so hold the flag while it runs or the
    // wander-off guard would kill the tour mid-transition.
    const heldBefore = store.isTourNavigation;
    store.isTourNavigation = true;
    try {
      await current.onEnter();
    } catch (error) {
      logger.warn(`[tour] onEnter for scene "${current.id}" failed`, error);
    } finally {
      store.isTourNavigation = heldBefore;
    }
    if (token !== resolveToken) {
      return;
    }
  }

  if (current.target) {
    // Scenes without a declared route expect their target on the current
    // page — miss fast so skip cascades don't feel like a hang.
    const el = await waitForTourElement(current.target, current.route ? 3000 : 2000);
    if (token !== resolveToken) {
      return;
    }
    if (!el) {
      logger.warn(`[tour] target "${current.target}" not found — skipping scene "${current.id}"`);
      store.skipUnresolvableStep();
      return;
    }
    // Targets taller than the viewport (e.g. a full statistics grid) can't be
    // centered — 'center' scrolls their top off-screen and the spotlight
    // swallows the page header above them. Align their top instead, so the
    // highlight starts exactly where the content does.
    const tall = el.getBoundingClientRect().height > viewportHeight.value * 0.7;
    el.scrollIntoView({
      block: tall ? 'start' : 'center',
      behavior: animate.value ? 'smooth' : 'auto',
    });
    targetEl.value = el;
  } else {
    targetEl.value = null;
  }

  isResolving.value = false;
  visible.value = true;
  // The scene is on screen — any tour-driven transition (including a Back
  // hold) is over; from here, route changes are the user's.
  store.isTourNavigation = false;
  store.markStepShown();

  if (current.interactive && current.target) {
    armInteractiveTarget(current.target);
  }
}

/**
 * Interactive scenes: the user's own click on the spotlighted target
 * advances the tour — the scene's script is skipped (the click IS the
 * action). Document-level capture survives the target being re-rendered.
 */
function armInteractiveTarget(key: string): void {
  const onClick = (event: MouseEvent): void => {
    const target = event.target;
    if (target instanceof Element && target.closest(`[data-tour="${key}"]`)) {
      interactiveCleanup?.();
      interactiveCleanup = null;
      store.next();
    }
  };
  document.addEventListener('click', onClick, { capture: true });
  interactiveCleanup = (): void => {
    document.removeEventListener('click', onClick, { capture: true });
  };
}

// --- User controls: Next plays the scene's script, then advances -----------

async function handleNext(): Promise<void> {
  const current = step.value;
  if (!current || controlsLocked.value) {
    return;
  }
  interactiveCleanup?.();
  interactiveCleanup = null;
  if (current.actions?.length) {
    const token = resolveToken;
    isPlaying.value = true;
    try {
      await runTourActions(current.actions, makeActionContext(token));
    } finally {
      if (token === resolveToken) {
        isPlaying.value = false;
        holeTargetEl.value = null;
      }
    }
    if (token !== resolveToken || !store.activeTourKey) {
      return;
    }
  }
  store.next();
}

/**
 * Back runs the current scene's backActions first (e.g. clicking the
 * wizard's own Back button) so the app state rewinds with the tour.
 */
async function handleBack(): Promise<void> {
  const current = step.value;
  if (!current || controlsLocked.value) {
    return;
  }
  // Backward transitions may navigate (a wizard's own Back button, or
  // history.back() to reach a scene on a parameterized route). All of that
  // is tour-driven: hold the flag until the previous scene actually shows
  // (resolveStep clears it) so the wander-off abort never fires mid-back.
  store.isTourNavigation = true;
  if (current.backActions?.length) {
    const token = resolveToken;
    isPlaying.value = true;
    try {
      await runTourActions(current.backActions, makeActionContext(token));
    } finally {
      if (token === resolveToken) {
        isPlaying.value = false;
        holeTargetEl.value = null;
      }
    }
    if (token !== resolveToken || !store.activeTourKey) {
      return;
    }
  }
  store.back();
}

watch(
  () => [store.activeTourKey, store.stepIndex] as const,
  () => {
    void resolveStep();
  },
  { immediate: true }
);

// --- Route integration: auto-start and wander-off -------------------------

watch(
  () => [route.name, store.isLoaded] as const,
  ([name]) => {
    if (isOfficeAddinHost()) {
      return;
    }
    if (store.activeTourKey) {
      // The tour's own navigation (engine push, or a scripted action mid-
      // playback) is expected — as is landing on the current scene's route
      // (e.g. an interactive click that navigates). ANY other route change
      // is the user driving, and the user always wins: end the tour rather
      // than fight or follow them.
      if (store.isTourNavigation || isPlaying.value) {
        return;
      }
      if (step.value?.route && name === step.value.route) {
        return;
      }
      logger.warn(`[tour] user navigated away mid-tour (to "${String(name)}") — ending tour`);
      store.abort();
      return;
    }
    store.maybeAutoStart(typeof name === 'string' ? name : undefined);
  },
  { immediate: true }
);

// --- Focus trap & keyboard -------------------------------------------------

let trap: FocusTrap | null = null;

watch(popoverEl, el => {
  trap?.deactivate();
  trap = null;
  if (!el) {
    return;
  }
  trap = createFocusTrap(el, {
    // Interactive scenes need real clicks outside the bubble.
    allowOutsideClick: true,
    escapeDeactivates: false,
    initialFocus: () => el,
    fallbackFocus: () => el,
  });
  try {
    trap.activate();
  } catch {
    // No tabbable content is fine — the container itself holds focus.
  }
});

function onKeydown(event: KeyboardEvent): void {
  if (!store.activeTourKey) {
    // Esc hides an unanswered invitation for this session (no record).
    if (store.pendingInvite && event.key === 'Escape') {
      handleHideInvite();
    }
    return;
  }
  if (event.key === 'Escape') {
    event.stopPropagation();
    handleDismiss();
    return;
  }
  const target = event.target;
  if (
    target instanceof HTMLElement &&
    (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
  ) {
    return;
  }
  switch (event.key) {
    case 'Enter': {
      // A focused button (Back, ✕, or Next itself) already acts on Enter —
      // stepping in would double-fire or fight the button's own click.
      if (target instanceof HTMLElement && target.closest('button')) {
        return;
      }
      event.preventDefault();
      void handleNext();
      break;
    }
    case 'ArrowRight': {
      void handleNext();
      break;
    }
    case 'ArrowLeft': {
      void handleBack();
      break;
    }
    default: {
      break;
    }
  }
}

// Focus "Show me" when the invitation opens so Enter accepts it directly.
watch(
  () => store.pendingInvite,
  async invite => {
    if (!invite) {
      return;
    }
    await nextTick();
    inviteEl.value?.querySelector<HTMLElement>('.n-button--primary-type')?.focus();
  }
);

// --- "Tours live here" teaching moment --------------------------------------

/**
 * Collapse a card into the topbar's tour button and blink it: whenever
 * someone waves a tour away (invite declined/hidden, tour exited), show
 * WHERE to come back to. A pixel-perfect clone of the card flies into the
 * button; the button then pulses for ~2s. No topbar (hidden layouts,
 * Office) = silent no-op.
 */
function flyToHelpButton(source: HTMLElement | null): void {
  const help = document.querySelector<HTMLElement>('[data-tour="topbar.help"]');
  if (!help) {
    return;
  }
  if (animate.value && source) {
    const from = source.getBoundingClientRect();
    const to = help.getBoundingClientRect();
    const ghost = source.cloneNode(true) as HTMLElement;
    ghost.classList.add('tour-fly-ghost');
    ghost.style.position = 'fixed';
    ghost.style.zIndex = '4000';
    ghost.style.margin = '0';
    ghost.style.left = `${from.left}px`;
    ghost.style.top = `${from.top}px`;
    ghost.style.width = `${from.width}px`;
    ghost.style.height = `${from.height}px`;
    // Source classes may position via transform (the centered invite) —
    // inline geometry above already places the clone, so reset it.
    ghost.style.transform = 'none';
    document.body.appendChild(ghost);
    // Commit the start geometry, then animate on the next frame.
    ghost.getBoundingClientRect();
    const dx = to.left + to.width / 2 - (from.left + from.width / 2);
    const dy = to.top + to.height / 2 - (from.top + from.height / 2);
    requestAnimationFrame(() => {
      ghost.style.transform = `translate(${dx}px, ${dy}px) scale(0.05)`;
      ghost.style.opacity = '0.2';
    });
    window.setTimeout(() => {
      ghost.remove();
    }, 700);
  }
  // Blink after the ghost lands (immediately under reduced motion).
  const delayMs = animate.value ? 550 : 0;
  window.setTimeout(() => {
    help.classList.remove('tour-help-blink');
    // Reflow so a rapid second blink restarts the animation.
    void help.offsetWidth;
    help.classList.add('tour-help-blink');
    window.setTimeout(() => {
      help.classList.remove('tour-help-blink');
    }, 2200);
  }, delayMs);
}

function handleDeclineInvite(): void {
  flyToHelpButton(inviteEl.value);
  store.declineInvite();
}

function handleHideInvite(): void {
  flyToHelpButton(inviteEl.value);
  store.clearInvite();
}

function handleDismiss(): void {
  flyToHelpButton(popoverEl.value);
  void store.dismiss();
}

// Attached unconditionally: a watch on activeTourKey misses transitions that
// happen during setup (auto-start), which left Esc dead on some flows.
onMounted(() => {
  window.addEventListener('keydown', onKeydown);
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown);
  interactiveCleanup?.();
  trap?.deactivate();
});
</script>

<template>
  <teleport to="body">
    <transition :name="animate ? 'tour-fade' : ''">
      <div v-if="visible" class="tour-overlay" role="presentation">
        <svg
          class="tour-overlay__backdrop"
          :width="viewportWidth"
          :height="viewportHeight"
          aria-hidden="true"
        >
          <defs>
            <mask id="tour-spotlight-mask">
              <rect width="100%" height="100%" fill="white" />
              <rect
                v-if="hole"
                class="tour-overlay__hole"
                :class="{ 'tour-overlay__hole--animated': animate }"
                :x="hole.x"
                :y="hole.y"
                :width="hole.width"
                :height="hole.height"
                rx="8"
                fill="black"
              />
            </mask>
          </defs>
          <rect
            width="100%"
            height="100%"
            class="tour-overlay__dim"
            mask="url(#tour-spotlight-mask)"
          />
        </svg>
        <!-- Click blockers: full page, except the open hole on interactive
             scenes. Scripted actions use synthetic events and are unaffected. -->
        <div
          v-for="(shield, index) in shields"
          :key="index"
          class="tour-overlay__shield"
          :style="{
            left: `${shield.x}px`,
            top: `${shield.y}px`,
            width: `${shield.width}px`,
            height: `${shield.height}px`,
          }"
        />
      </div>
    </transition>

    <!-- First-visit invitation: the tour never starts itself. Centered over a
         dimmed, blurred backdrop so it can't be missed; clicking outside (or
         Esc) hides it for the session without recording a decline. -->
    <transition :name="animate ? 'tour-fade' : ''">
      <div
        v-if="store.pendingInvite && !store.activeTourKey"
        class="tour-invite-backdrop"
        @click="handleHideInvite"
      />
    </transition>
    <transition :name="animate ? 'tour-invite-pop' : ''">
      <div
        v-if="store.pendingInvite && !store.activeTourKey"
        ref="inviteEl"
        class="tour-invite"
        role="dialog"
        aria-modal="true"
        aria-label="Tour invitation"
      >
        <div class="tour-invite__glow" aria-hidden="true" />
        <n-card size="small" class="tour-popover__card tour-invite__card">
          <n-flex vertical align="center" :size="10">
            <n-text depth="3" class="tour-popover__eyebrow">Guided tour</n-text>
            <n-text strong class="tour-invite__title">
              Want a quick tour of {{ store.pendingInvite.title }}?
            </n-text>
            <n-text v-if="store.pendingInvite.description" class="tour-invite__body">
              {{ store.pendingInvite.description }}
            </n-text>
            <n-flex justify="center" align="center" :size="12" class="tour-invite__buttons">
              <n-button quaternary @click="handleDeclineInvite">Not now</n-button>
              <n-button type="primary" @click="store.acceptInvite()">Show me</n-button>
            </n-flex>
          </n-flex>
        </n-card>
      </div>
    </transition>

    <transition :name="animate ? 'tour-pop' : ''">
      <div
        v-if="showPopover && step"
        ref="popoverEl"
        class="tour-popover"
        :class="{ 'tour-popover--centered': isCentered }"
        :style="isCentered ? undefined : floatingStyles"
        role="dialog"
        aria-modal="false"
        aria-labelledby="tour-step-title"
        aria-describedby="tour-step-body"
        tabindex="-1"
      >
        <!-- Even 12px all round so the ✕ sits equidistant from the top and
             right edges (naive's small card is 12px 16px). Centered cards keep
             their own roomier padding (see the scoped styles). -->
        <n-card
          size="small"
          class="tour-popover__card"
          :content-style="isCentered ? undefined : { padding: '12px' }"
        >
          <n-flex vertical :size="8">
            <n-flex justify="space-between" align="center" :wrap="false">
              <n-text depth="3" class="tour-popover__eyebrow">
                {{ store.activeTour?.title }}
              </n-text>
              <n-button quaternary circle size="tiny" aria-label="Exit tour" @click="handleDismiss">
                ✕
              </n-button>
            </n-flex>
            <n-text id="tour-step-title" strong class="tour-popover__title">
              {{ step.title }}
            </n-text>
            <n-text id="tour-step-body" class="tour-popover__body">{{ stepBody }}</n-text>
            <!-- Keyboard hint: first scene of a run only — teach once, then
                 get out of the way. -->
            <n-text v-if="store.stepIndex === 0" depth="3" class="tour-popover__kbd-hint">
              Tip: <kbd>→</kbd> or <kbd>Enter</kbd> next · <kbd>←</kbd> back · <kbd>Esc</kbd> leave
            </n-text>
            <!-- Finale only: hand the user their next tour. Untaken tours
                 lead; taken ones follow for a second look. -->
            <template v-if="isLastStep && (nextTours.fresh.length || nextTours.taken.length)">
              <n-divider class="tour-popover__next-divider" />
              <n-flex v-if="nextTours.fresh.length" vertical :size="6" align="center">
                <n-text depth="3" class="tour-popover__next-label">Keep exploring</n-text>
                <n-flex justify="center" :size="6">
                  <n-button
                    v-for="tour in nextTours.fresh"
                    :key="tour.key"
                    size="tiny"
                    round
                    secondary
                    type="primary"
                    :disabled="controlsLocked"
                    @click="handleStartAnotherTour(tour.key)"
                  >
                    {{ tour.title }}
                  </n-button>
                </n-flex>
              </n-flex>
              <n-flex v-if="nextTours.taken.length" vertical :size="6" align="center">
                <n-text depth="3" class="tour-popover__next-label">Take another look</n-text>
                <n-flex justify="center" :size="6">
                  <n-button
                    v-for="tour in nextTours.taken"
                    :key="tour.key"
                    size="tiny"
                    round
                    quaternary
                    :disabled="controlsLocked"
                    @click="handleStartAnotherTour(tour.key)"
                  >
                    ✓ {{ tour.title }}
                  </n-button>
                </n-flex>
              </n-flex>
            </template>
            <n-flex justify="space-between" align="center">
              <n-text depth="3">{{ store.stepIndex + 1 }} of {{ totalSteps }}</n-text>
              <n-flex align="center" :size="8">
                <n-button
                  v-if="store.stepIndex > 0"
                  quaternary
                  size="small"
                  :disabled="controlsLocked"
                  @click="handleBack"
                >
                  Back
                </n-button>
                <n-button
                  type="primary"
                  size="small"
                  :disabled="controlsLocked"
                  @click="handleNext"
                >
                  {{ store.stepIndex + 1 === totalSteps ? 'Done' : 'Next' }}
                </n-button>
              </n-flex>
            </n-flex>
          </n-flex>
        </n-card>
      </div>
    </transition>
  </teleport>
</template>

<style scoped>
/* Raw CSS is unavoidable here: fixed full-viewport overlay layering, the SVG
   spotlight mask, and floating-ui positioning are outside Naive UI's domain.
   Everything inside the popover card is Naive components. */

.tour-overlay {
  position: fixed;
  inset: 0;
  /* Above Naive modals (~2000+, auto-incremented): scripted scenes open real
     modals (e.g. the recipient picker) and the spotlight must frame elements
     INSIDE them — below the tour layer the dim/popover vanish behind the
     modal. Naive message toasts (~6000) stay above. */
  z-index: 3500;
  pointer-events: none;
}

.tour-overlay__backdrop {
  position: absolute;
  inset: 0;
}

.tour-overlay__dim {
  fill: rgba(15, 23, 42, 0.55);
}

:global(.dark) .tour-overlay__dim {
  fill: rgba(0, 0, 0, 0.65);
}

.tour-overlay__hole--animated {
  transition:
    x 0.35s cubic-bezier(0.4, 0, 0.2, 1),
    y 0.35s cubic-bezier(0.4, 0, 0.2, 1),
    width 0.35s cubic-bezier(0.4, 0, 0.2, 1),
    height 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.tour-overlay__shield {
  position: absolute;
  pointer-events: auto;
}

.tour-popover {
  position: absolute;
  z-index: 3510;
  width: max-content;
  /* Never wider than the viewport, whatever the screen size. */
  max-width: min(340px, calc(100vw - 32px));
  outline: none;
}

/* First-visit invitation: centered and unmissable. The backdrop dims and
   blurs the page; the card wears a slowly breathing gradient glow. */
.tour-invite-backdrop {
  position: fixed;
  inset: 0;
  /* Above the bottom-right progress panels (1000), below Naive modals. */
  z-index: 1590;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(1px);
}

:global(.dark) .tour-invite-backdrop {
  background: rgba(0, 0, 0, 0.55);
}

.tour-invite {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1600;
  width: min(440px, calc(100vw - 32px));
}

/* Animated gradient BORDER: a conic gradient sweeps around a thin ring just
   outside the card's edges; the card body covers the center, so only the
   crisp rim shows. The sweep angle is a registered custom property — that is
   what makes the rotation animatable (without @property it would snap). */
@property --tour-invite-angle {
  syntax: '<angle>';
  initial-value: 0deg;
  inherits: false;
}

.tour-invite__glow {
  position: absolute;
  inset: -2px;
  border-radius: 12px;
  /* A quiet brand-blue border with a single luminous "comet" orbiting it:
     long soft tail building into a bright head, then a clean cut. Stays in
     the blue family on purpose — one traveling light, not a color wheel. */
  background-color: rgba(37, 99, 235, 0.22);
  background-image: conic-gradient(
    from var(--tour-invite-angle, 0deg),
    rgba(37, 99, 235, 0) 0deg,
    rgba(37, 99, 235, 0) 240deg,
    rgba(37, 99, 235, 0.75) 320deg,
    #93c5fd 352deg,
    #dbeafe 358deg,
    rgba(37, 99, 235, 0) 360deg
  );
  animation: tour-invite-spin 4s linear infinite;
  pointer-events: none;
}

@keyframes tour-invite-spin {
  to {
    --tour-invite-angle: 360deg;
  }
}

.tour-invite__card {
  /* Paint above the positioned glow ring so only the rim stays visible. */
  position: relative;
}

.tour-invite__card :deep(.n-card__content) {
  padding: 22px 28px 20px;
}

.tour-invite__title {
  display: block;
  text-align: center;
  font-size: 18px;
  line-height: 1.4;
  letter-spacing: -0.01em;
}

.tour-invite__body {
  display: block;
  text-align: center;
  line-height: 1.6;
}

.tour-invite__buttons {
  margin-top: 6px;
}

.tour-invite-pop-enter-active {
  transition:
    opacity 0.3s ease,
    transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.tour-invite-pop-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}

.tour-invite-pop-enter-from,
.tour-invite-pop-leave-to {
  opacity: 0;
  transform: translate(-50%, calc(-50% + 12px)) scale(0.95);
}

.tour-popover--centered {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  /* Target-less cards are general announcements (welcome, finale): a wider,
     roomier hero treatment with centered copy. */
  max-width: min(500px, calc(100vw - 32px));
  width: 100%;
}

.tour-popover--centered .tour-popover__card :deep(.n-card__content) {
  padding: 20px 28px 18px;
}

.tour-popover--centered .tour-popover__title {
  display: block;
  text-align: center;
  font-size: 19px;
  line-height: 1.35;
  margin-top: 2px;
}

.tour-popover--centered .tour-popover__body {
  display: block;
  text-align: center;
  font-size: 14px;
  line-height: 1.65;
  margin-bottom: 6px;
}

.tour-popover__card {
  border-radius: 10px;
  box-shadow:
    0 12px 40px -12px rgba(15, 23, 42, 0.4),
    0 4px 12px -6px rgba(15, 23, 42, 0.25);
}

.tour-popover__eyebrow {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.tour-popover__title {
  font-size: 15px;
  line-height: 1.4;
}

.tour-popover__kbd-hint {
  font-size: 11.5px;
}

.tour-popover__next-divider {
  margin: 6px 0 2px;
}

.tour-popover__next-label {
  font-size: 10px;
  font-weight: 650;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.tour-popover--centered .tour-popover__kbd-hint {
  text-align: center;
}

.tour-popover__kbd-hint kbd {
  display: inline-block;
  padding: 0 5px;
  border: 1px solid rgba(100, 116, 139, 0.35);
  border-bottom-width: 2px;
  border-radius: 4px;
  font-family: inherit;
  font-size: 10.5px;
  line-height: 1.6;
}

.tour-fade-enter-active,
.tour-fade-leave-active {
  transition: opacity 0.25s ease;
}

.tour-fade-enter-from,
.tour-fade-leave-to {
  opacity: 0;
}

.tour-pop-enter-active,
.tour-pop-leave-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.tour-pop-enter-from,
.tour-pop-leave-to {
  opacity: 0;
}

.tour-pop-enter-from:not(.tour-popover--centered),
.tour-pop-leave-to:not(.tour-popover--centered) {
  transform: translateY(6px);
}

@media (prefers-reduced-motion: reduce) {
  .tour-overlay__hole--animated,
  .tour-fade-enter-active,
  .tour-fade-leave-active,
  .tour-pop-enter-active,
  .tour-pop-leave-active,
  .tour-invite-pop-enter-active,
  .tour-invite-pop-leave-active {
    transition: none;
  }

  .tour-invite__glow {
    animation: none;
  }
}
</style>

<style>
/* Global (unscoped) on purpose: the pulse lands on arbitrary page elements
   the playback acts on, outside this component's scoped subtree. */
.tour-action-pulse {
  animation: tour-action-pulse 0.7s ease;
}

/* Clone of the dismissed invite/bubble flying into the topbar's tour
   button — appended to body, so it must live in global CSS. The clone keeps
   the card's own classes for its looks; this only drives the flight. */
.tour-fly-ghost {
  z-index: 4000;
  pointer-events: none;
  opacity: 1;
  transform-origin: center;
  transition:
    transform 0.6s cubic-bezier(0.55, 0, 0.65, 0.5),
    opacity 0.6s ease;
}

/* Blink on the tour button after the ghost lands: "tours live here".
   Applied to the topbar element, outside this component's subtree. */
.tour-help-blink {
  /* Two pulses over 2 seconds. */
  animation: tour-help-blink 1s ease 2;
  border-radius: 50%;
}

@keyframes tour-help-blink {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(37, 99, 235, 0);
    background-color: transparent;
  }
  35% {
    box-shadow: 0 0 0 10px rgba(37, 99, 235, 0.35);
    background-color: rgba(37, 99, 235, 0.18);
  }
  70% {
    box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.15);
    background-color: rgba(37, 99, 235, 0.08);
  }
}

@keyframes tour-action-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.55);
  }
  100% {
    box-shadow: 0 0 0 14px rgba(37, 99, 235, 0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .tour-action-pulse,
  .tour-help-blink {
    animation: none;
  }

  .tour-fly-ghost {
    display: none;
  }
}
</style>
